import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  useTheme,
  Stack,
} from '@mui/material';
import { PlayArrow, Schedule, Refresh } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { testRemindersForUser } from '../utils/automatedReminders';
import { supabase } from '../lib/supabase';

const AutomatedReminderTest: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [isTesting, setIsTesting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; data: any } | null>(null);

  // Hide in production - only show in development
  // Can be overridden with REACT_APP_SHOW_TEST_FEATURES=true
  if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_SHOW_TEST_FEATURES !== 'true') {
    return null;
  }

  const handleTestAutomatedReminders = async () => {
    if (!user) return;

    setIsTesting(true);
    setResult(null);

    try {
      console.log('🧪 Testing automated reminder system...');
      
      const success = await testRemindersForUser(user.id);

      if (success) {
        setResult({ 
          success: true, 
          message: '✅ Automated reminder test completed successfully! Check your email for the reminder.' 
        });
      } else {
        setResult({ 
          success: false, 
          message: '❌ Automated reminder test failed. Check console for details.' 
        });
      }
    } catch (error) {
      console.error('❌ Error testing automated reminders:', error);
      setResult({ 
        success: false, 
        message: `❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    } finally {
      setIsTesting(false);
    }
  };

  const testDailyReminders = async () => {
    setTestLoading(true);
    setTestResult(null);
    
    try {
      console.log('🧪 Testing daily-reminders function...');
      
      const { data, error } = await supabase.functions.invoke('daily-reminders', {
        body: { test: true }
      });

      if (error) {
        throw error;
      }

      console.log('✅ Daily reminders test result:', data);
      setTestResult({
        success: true,
        message: 'Daily reminders function is working!',
        data: data
      });
    } catch (error: unknown) {
      console.error('❌ Daily reminders test failed:', error);
      setTestResult({
        success: false,
        message: `Daily reminders test failed: ${error instanceof Error ? (error as Error).message : 'Unknown error'}`,
        data: null
      });
    } finally {
      setTestLoading(false);
    }
  };

  const debugReminderSettings = async () => {
    if (!user) return;
    
    try {
      console.log('🔍 Debugging reminder settings...');
      
      // Get user preferences
      const { data: preferences, error: prefError } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (prefError) throw prefError;

      // Get active subscriptions
      const { data: subscriptions, error: subError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (subError) throw subError;

      // Calculate which subscriptions should get reminders today
      const today = new Date();
      const subscriptionsNeedingReminders = subscriptions?.filter(subscription => {
        const renewalDate = new Date(subscription.next_renewal_date);
        const reminderDate = new Date(renewalDate);
        reminderDate.setDate(renewalDate.getDate() - (preferences?.reminder_days_before || 3));

        return (
          today.getFullYear() === reminderDate.getFullYear() &&
          today.getMonth() === reminderDate.getMonth() &&
          today.getDate() === reminderDate.getDate()
        );
      }) || [];

      const debugInfo = {
        userPreferences: preferences,
        activeSubscriptions: subscriptions?.length || 0,
        reminderDaysBefore: preferences?.reminder_days_before,
        emailNotificationsEnabled: preferences?.email_notifications,
        subscriptionsNeedingRemindersToday: subscriptionsNeedingReminders.length,
        today: today.toISOString(),
        nextRenewalDates: subscriptions?.map(sub => ({
          name: sub.name,
          nextRenewal: sub.next_renewal_date,
          daysUntilRenewal: Math.ceil((new Date(sub.next_renewal_date).getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        })) || []
      };

      console.log('🔍 Debug info:', debugInfo);
      setTestResult({
        success: true,
        message: 'Debug info retrieved successfully',
        data: debugInfo
      });

    } catch (error: unknown) {
      console.error('❌ Debug failed:', error);
      setTestResult({
        success: false,
        message: `Debug failed: ${error instanceof Error ? (error as Error).message : 'Unknown error'}`,
        data: null
      });
    }
  };

  if (!user) return null;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Schedule sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h6">Automated Reminder System Test</Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Test the automated reminder system that will run daily based on your reminder settings.
          This simulates what happens when the system automatically checks for subscriptions needing reminders.
        </Typography>

        {result && (
          <Alert 
            severity={result.success ? 'success' : 'error'} 
            sx={{ mb: 2 }}
          >
            {result.message}
          </Alert>
        )}

        <Button
          variant="contained"
          startIcon={isTesting ? <CircularProgress size={20} /> : <PlayArrow />}
          onClick={handleTestAutomatedReminders}
          disabled={isTesting}
          fullWidth
          sx={{
            color: theme.palette.mode === 'dark' ? 'black' : 'white',
            '&:hover': {
              color: theme.palette.mode === 'dark' ? 'black' : 'white',
            },
          }}
        >
          {isTesting ? 'Testing Automated System...' : 'Test Automated Reminder System'}
        </Button>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          This will test the automated reminder logic using your current reminder settings and active subscriptions.
        </Typography>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>How it works:</strong> The system checks your reminder preferences and finds subscriptions 
            that need reminders based on your "Days before renewal" setting. In production, this runs automatically 
            every day via a scheduled task.
          </Typography>
        </Alert>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          🧪 Automated Reminder System Test
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Test the automated daily reminder system that runs via Supabase Edge Functions.
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => user && testRemindersForUser(user.id)}
            disabled={testLoading || !user}
            startIcon={<Refresh />}
          >
            Test User Reminders
          </Button>
          
          <Button
            variant="outlined"
            onClick={testDailyReminders}
            disabled={testLoading}
            startIcon={<Refresh />}
          >
            Test Daily Reminders Function
          </Button>

          <Button
            variant="outlined"
            onClick={debugReminderSettings}
            disabled={testLoading || !user}
            startIcon={<Refresh />}
          >
            Debug Reminder Settings
          </Button>
        </Stack>

        {testResult && (
          <Alert 
            severity={testResult.success ? 'success' : 'error'} 
            sx={{ mt: 2 }}
          >
            {testResult.message}
            {testResult.data && (
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.8em', marginTop: '10px' }}>
                {JSON.stringify(testResult.data, null, 2)}
              </pre>
            )}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default AutomatedReminderTest;
